/**
 * Offline Manager Service
 * 
 * Main service for managing offline functionality.
 * Coordinates caching, sync queue, and network monitoring.
 */

import { networkMonitor, type NetworkStatus } from './networkMonitor';
import { offlineStorage, tripStorage, metadataStorage, type OfflineMetadata } from './offlineStorage';
import { syncQueue, type SyncAction } from './syncQueue';
import './syncQueueBootstrap';
import type { Trip } from './trips';

export type OfflineTripData = {
  trip: Trip;
  expenses?: unknown[];
  bookings?: unknown[];
  itinerary?: unknown[];
  cachedAt: number;
};

export type OfflineAction = SyncAction;

class OfflineManager {
  /**
   * Check if device is currently offline
   */
  isOffline(): boolean {
    return networkMonitor.isOffline();
  }

  /**
   * Check if device is currently online
   */
  isOnline(): boolean {
    return networkMonitor.isOnline();
  }

  /**
   * Get current network status
   */
  async getNetworkStatus(): Promise<NetworkStatus> {
    return await networkMonitor.getStatus();
  }

  /**
   * Subscribe to network status changes
   */
  onNetworkStatusChange(listener: (status: NetworkStatus) => void): () => void {
    return networkMonitor.addListener(listener);
  }

  /**
   * Cache trip data for offline access
   */
  async cacheTripData(tripId: string, data: {
    trip: Trip;
    expenses?: unknown[];
    bookings?: unknown[];
    itinerary?: unknown[];
  }): Promise<void> {
    try {
      const tripData: OfflineTripData = {
        ...data,
        cachedAt: Date.now(),
      };

      await tripStorage.setTripData(tripId, tripData);
      
      // Update metadata
      const metadata = await metadataStorage.getMetadata() || {
        lastSync: 0,
        version: '1.0.0',
        tripIds: [],
      };
      
      if (!metadata.tripIds.includes(tripId)) {
        metadata.tripIds.push(tripId);
      }
      
      await metadataStorage.updateMetadata({
        tripIds: metadata.tripIds,
      });

      console.log(`[OfflineManager] Cached trip data for trip: ${tripId}`);
    } catch (error) {
      console.error(`[OfflineManager] Error caching trip data for ${tripId}:`, error);
      throw error;
    }
  }

  /**
   * Get offline trip data
   */
  async getOfflineTrip(tripId: string): Promise<OfflineTripData | null> {
    try {
      return await tripStorage.getTripData<OfflineTripData>(tripId);
    } catch (error) {
      console.error(`[OfflineManager] Error getting offline trip ${tripId}:`, error);
      return null;
    }
  }

  /**
   * Get all cached trip IDs
   */
  async getCachedTripIds(): Promise<string[]> {
    return await tripStorage.getCachedTripIds();
  }

  /**
   * Remove trip from cache
   */
  async removeCachedTrip(tripId: string): Promise<void> {
    try {
      await tripStorage.removeTrip(tripId);
      
      // Update metadata
      const metadata = await metadataStorage.getMetadata();
      if (metadata) {
        const updatedTripIds = metadata.tripIds.filter(id => id !== tripId);
        await metadataStorage.updateMetadata({ tripIds: updatedTripIds });
      }
    } catch (error) {
      console.error(`[OfflineManager] Error removing cached trip ${tripId}:`, error);
    }
  }

  /**
   * Queue an action for sync when online
   */
  async queueAction(action: Omit<SyncAction, 'id' | 'timestamp' | 'retries'>): Promise<void> {
    try {
      await syncQueue.queueAction(action);
    } catch (error) {
      console.error('[OfflineManager] Error queueing action:', error);
      throw error;
    }
  }

  /**
   * Get all queued actions
   */
  async getQueuedActions(): Promise<SyncAction[]> {
    return await syncQueue.getQueuedActions();
  }

  /**
   * Get queue size
   */
  async getQueueSize(): Promise<number> {
    return await syncQueue.getQueueSize();
  }

  /**
   * Sync queued actions when online
   */
  async syncWhenOnline(): Promise<void> {
    if (!this.isOnline()) {
      console.log('[OfflineManager] Device is offline, cannot sync');
      return;
    }

    try {
      await syncQueue.processQueue();
      
      // Update last sync time
      await metadataStorage.updateMetadata({
        lastSync: Date.now(),
      });
    } catch (error) {
      console.error('[OfflineManager] Error syncing when online:', error);
      throw error;
    }
  }

  /**
   * Get offline metadata
   */
  async getMetadata(): Promise<OfflineMetadata | null> {
    return await metadataStorage.getMetadata();
  }

  /**
   * Clear all offline data (use with caution)
   */
  async clearAllOfflineData(): Promise<void> {
    try {
      await offlineStorage.clearAll();
      await syncQueue.clearQueue();
      console.log('[OfflineManager] Cleared all offline data');
    } catch (error) {
      console.error('[OfflineManager] Error clearing offline data:', error);
      throw error;
    }
  }

  /**
   * Check if a trip is cached
   */
  async isTripCached(tripId: string): Promise<boolean> {
    const trip = await tripStorage.getTrip(tripId);
    return trip !== null;
  }

  /**
   * Get cache size information
   */
  async getCacheInfo(): Promise<{
    tripCount: number;
    queueSize: number;
    lastSync: number;
  }> {
    const tripIds = await this.getCachedTripIds();
    const queueSize = await this.getQueueSize();
    const metadata = await this.getMetadata();
    
    return {
      tripCount: tripIds.length,
      queueSize,
      lastSync: metadata?.lastSync || 0,
    };
  }

  /**
   * Subscribe to sync queue processing status
   */
  onSyncProcessingChange(listener: (isProcessing: boolean) => void): () => void {
    return syncQueue.onProcessingChange(listener);
  }

  /**
   * Check if sync queue is currently processing
   */
  isSyncing(): boolean {
    return syncQueue.isProcessingQueue();
  }
}

// Singleton instance
export const offlineManager = new OfflineManager();

/** NetInfo can flip "online" before the Firestore client finishes reconnecting; debounce drains the sync queue slightly later. */
const ONLINE_SYNC_DEBOUNCE_MS = 1200;
let onlineSyncDebounceTimer: ReturnType<typeof setTimeout> | null = null;

networkMonitor.addListener((status) => {
  // Only schedule kicks when connectivity is plausible; syncWhenOnline still verifies `networkMonitor.isOnline()`.
  if (!status.isConnected) {
    if (onlineSyncDebounceTimer) {
      clearTimeout(onlineSyncDebounceTimer);
      onlineSyncDebounceTimer = null;
    }
    return;
  }
  if (syncQueue.isProcessingQueue()) return;

  if (onlineSyncDebounceTimer) {
    clearTimeout(onlineSyncDebounceTimer);
  }
  onlineSyncDebounceTimer = setTimeout(() => {
    onlineSyncDebounceTimer = null;
    if (!networkMonitor.isOnline()) return;
    if (syncQueue.isProcessingQueue()) return;
    offlineManager.syncWhenOnline().catch((err) => {
      console.error('[OfflineManager] Error auto-syncing on network connect:', err);
    });
    void Promise.all([import('./trips'), import('@/lib/firebase')]).then(([tripsMod, fb]) => {
      const uid = fb.auth?.currentUser?.uid;
      if (uid && networkMonitor.isOnline()) {
        void tripsMod.refreshTripListFromServer(uid).catch(() => {});
      }
    });
  }, ONLINE_SYNC_DEBOUNCE_MS);
});

